import { defineStore } from 'pinia';
import { ref } from 'vue';

export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  hideCancelButton?: boolean;
}

export interface PromptDialogOptions {
  title?: string;
  message: string;
  defaultValue?: string;
  confirmText?: string;
  cancelText?: string;
}

export const useDialogStore = defineStore('dialogs', () => {
  // Confirm Dialog
  const confirmDialog = ref<{
    isOpen: boolean;
    options: ConfirmDialogOptions;
    resolve: ((value: boolean) => void) | null;
  }>({
    isOpen: false,
    options: { message: '' },
    resolve: null,
  });

  // Prompt Dialog
  const promptDialog = ref<{
    isOpen: boolean;
    options: PromptDialogOptions;
    resolve: ((value: string | null) => void) | null;
  }>({
    isOpen: false,
    options: { message: '' },
    resolve: null,
  });

  async function confirm(message: string, options?: Partial<ConfirmDialogOptions>): Promise<boolean> {
    return new Promise((resolve) => {
      confirmDialog.value = {
        isOpen: true,
        options: { message, ...options },
        resolve,
      };
    });
  }

  async function alert(message: string, options?: Partial<Omit<ConfirmDialogOptions, 'hideCancelButton'>>): Promise<void> {
    return new Promise((resolve) => {
      confirmDialog.value = {
        isOpen: true,
        options: { message, ...options, hideCancelButton: true },
        resolve: (): void => resolve(),
      };
    });
  }

  function resolveConfirm(value: boolean): void {
    if (confirmDialog.value.resolve) {
      confirmDialog.value.resolve(value);
    }
    confirmDialog.value.isOpen = false;
    confirmDialog.value.resolve = null;
  }

  async function prompt(message: string, options?: Partial<PromptDialogOptions>): Promise<string | null> {
    return new Promise((resolve) => {
      promptDialog.value = {
        isOpen: true,
        options: { message, ...options },
        resolve,
      };
    });
  }

  function resolvePrompt(value: string | null): void {
    if (promptDialog.value.resolve) {
      promptDialog.value.resolve(value);
    }
    promptDialog.value.isOpen = false;
    promptDialog.value.resolve = null;
  }

  return {
    confirmDialog,
    promptDialog,
    confirm,
    alert,
    resolveConfirm,
    prompt,
    resolvePrompt,
  };
});
